import React, { useState, useEffect } from "react";
import { Box, Container, Grid, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import { ShoppingCart, LocalShipping, CheckCircle, PendingActions } from "@mui/icons-material";
import { getStats } from "../services/api";

const Dashboard = () => {
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchStats();
	}, []);

	const fetchStats = async () => {
		try {
			const response = await getStats();
			setStats(response.data);
		} catch (error) {
			console.error("Error fetching stats:", error);
		} finally {
			setLoading(false);
		}
	};

	const StatCard = ({ title, value, icon }) => (
		<Card
			elevation={0}
			sx={{
				height: "100%",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 3,
				transition: "all 0.3s ease",
				"&:hover": {
					transform: "translateY(-2px)",
					boxShadow: 2,
				},
			}}>
			<CardContent sx={{ p: 4 }}>
				<Box display="flex" flexDirection="column" gap={2}>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: 2,
							backgroundColor: "rgba(0, 0, 0, 0.05)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mb: 1,
							color: "text.primary",
							fontSize: "1.5rem",
						}}>
						{icon}
					</Box>

					<Box>
						<Typography
							color="text.secondary"
							variant="body2"
							sx={{
								fontSize: "0.8125rem",
								fontWeight: 400,
								mb: 1,
								textTransform: "uppercase",
								letterSpacing: "0.05em",
							}}>
							{title}
						</Typography>

						<Typography
							variant="h3"
							component="div"
							sx={{
								fontWeight: 600,
								fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
								letterSpacing: "-0.02em",
								lineHeight: 1.1,
							}}>
							{value?.toLocaleString() || "0"}
						</Typography>
					</Box>
				</Box>
			</CardContent>
		</Card>
	);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
			<Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
				<Typography
					variant="h1"
					sx={{
						fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
						fontWeight: 600,
						letterSpacing: "-0.02em",
						mb: 2,
						lineHeight: 1.1,
					}}>
					Sales Order
				</Typography>

				<Typography
					variant="h1"
					sx={{
						fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
						fontWeight: 600,
						letterSpacing: "-0.02em",
						lineHeight: 1.1,
					}}>
					Tracking
				</Typography>

				<Typography
					variant="body1"
					color="text.secondary"
					sx={{
						mt: 2,
						fontSize: "1.125rem",
						maxWidth: "600px",
					}}>
					Monitor your sales orders from creation to delivery with real-time tracking and TAT analysis.
				</Typography>
			</Box>

			<Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard title="Total Orders" value={stats?.total_sos} icon={<ShoppingCart />} />
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<StatCard title="Dispatched" value={stats?.dispatched} icon={<LocalShipping />} />
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<StatCard title="Goods Received" value={stats?.goods_received} icon={<CheckCircle />} />
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<StatCard title="Pending" value={stats?.pending} icon={<PendingActions />} />
				</Grid>
			</Grid>
		</Container>
	);
};

export default Dashboard;
